import { useEffect, useId, useRef, useState } from 'react';
import { useSession } from '../../state/useSession';
import { ApiError } from '../../api/client';
import { TAKEN_DOWN_MESSAGE, TakenDownError, makePrivate, relistApp } from '../../api/publish';
import type { Project } from '@/storage/ProjectRepository';
import type { AppDefinition } from '@/types/app';
import { SITE_STATUS_LABEL, takeOfflineConfirmation } from '@/utils/websiteSheet';
import { Button, Chip } from '../../ui';
import { SAVED_BUT_OFFLINE, liveState, publishLatest, recordPrivate, type LivePublish } from '../../state/livePage';
import { SITE_STATUS_MEANING, type SiteCheck } from '../../state/siteStatus';

/**
 * Publishing is the point of the product: it ends in a URL you bookmark.
 *
 * The sequence mirrors the phone's publishWebEdit exactly — assemble and upload, then
 * prefer the stable saddle URL and fall back to the direct upload link — and the result
 * is written back onto the project so a reload still knows where the app lives.
 *
 * Every failure names its own reason. WebAppPublishError distinguishes five, and
 * collapsing them into "publishing failed" would hide the two the person can act on
 * (not signed in, no backend).
 *
 * A chat edit publishes its own change (see ChatPanel), so "Publish changes" is not the way
 * an edit goes live any more: it is offered only when the page is BEHIND — an edit whose
 * publish failed, or a record from before edits published themselves. A button that is
 * always there says the page always needs it.
 *
 * The page's state, and the way out of it, come first: Live (or Password) with Take offline,
 * Offline with Put it back online — the phone's words. The owner could not tell whether their
 * site was up, and the only way down was "Make private" at the bottom of the panel, with no way
 * back once the page was reloaded except a Publish button that read like making a new site.
 * That state is the SERVER's (`site`), never this record's: see state/siteStatus.ts.
 */
export default function PublishPanel({
  project,
  editing = false,
  site,
  onSiteChange,
}: {
  project: Project;
  /**
   * A chat edit is being made, and will publish itself when it lands. Nothing here publishes or
   * takes the page down meanwhile: for an app with no page yet, two publishes at once register
   * it twice, and the second takes the address `<slug>-2`; and a Take offline that finishes
   * before the edit's publish is followed by that publish writing back onto the record.
   */
  editing?: boolean;
  /** Whether the page is up, as the server said when the editor asked. */
  site: SiteCheck;
  /**
   * What this panel changed the page to. Only ever from a server's answer: a publish's own
   * response, or the takedown calls having succeeded.
   */
  onSiteChange: (next: SiteCheck) => void;
}) {
  const { user } = useSession();
  const definition = project.definition as AppDefinition;
  const hostedAppId = definition.hostedAppId ?? '';
  const known = site.kind === 'known' ? site : null;
  const offline = known?.status === 'offline';
  const headingId = useId();
  const [url, setUrl] = useState<string | null>(definition.webUrl ?? null);
  /*
   * Follows the record, because this panel is no longer the only thing that publishes: a chat
   * edit can give a never-published app its address, and the link belongs here when it does.
   */
  useEffect(() => {
    setUrl(definition.webUrl ?? null);
  }, [definition.webUrl]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [takingDown, setTakingDown] = useState(false);
  const [takenDown, setTakenDown] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  /**
   * Assemble on the server, then host — the ONE content-update path, which a chat edit takes
   * too (state/livePage.ts). Shared by Publish, Publish changes and Put it back online.
   */
  async function publishContent(
    opts: { putOnline?: boolean } = {},
    relisted = false,
  ): Promise<LivePublish> {
    /*
     * "Publish changes" is a content update, which keeps a page its owner took offline offline
     * and a password page behind its password; sending 'public' there re-opened both on every
     * edit. So a visibility is named only when the person asked for the page to be UP.
     *
     * AppPublished is counted inside publishLatest, from the record as stored, so an app a chat
     * edit put online first is not counted a second time here.
     */
    const published = await publishLatest(project.id, user?.id ?? '', opts);
    setUrl(published.siteUrl);
    /*
     * The server's answer, so the status can follow it. A content update keeps a password; a
     * publish that put the page up named it public. Only an explicit false is a page that is off.
     */
    // Unasked (a first publish), it is not in Discover: every app registered here is created
    // unlisted. The editor asks the server again as soon as the app has an id to ask with.
    const listed = relisted || (known?.listed ?? false);
    onSiteChange(
      published.webPublished === false
        ? { kind: 'known', status: 'offline', listed, takenDown: false }
        : {
            kind: 'known',
            status: !opts.putOnline && known?.status === 'password' ? 'password' : 'live',
            listed,
            takenDown: false,
            siteUrl: published.siteUrl,
          },
    );
    return published;
  }

  /**
   * Publish, and when the server answers that the app is private, relist it and put it up.
   *
   * The server switches on only a page that is not private (`unlistedAt`), and relisting is the
   * one way to clear that. An app Take offline found already off Discover is not made private
   * (see makePrivate), so for those this goes straight through and never lists them.
   */
  async function publishOrRelist(putOnline: boolean): Promise<LivePublish> {
    try {
      return await publishContent({ putOnline });
    } catch (err) {
      if (!(err instanceof ApiError && err.code === 'unlisted')) throw err;
      await relistApp(hostedAppId);
      return publishContent({ putOnline: true }, true);
    }
  }

  /** The server's refusal of an app a moderator took down: not something a retry can fix. */
  function isTakenDown(err: unknown): boolean {
    return err instanceof TakenDownError
      || (err instanceof ApiError && (err.code === 'taken_down' || err.takenDown === true));
  }

  function showTakenDown(err: unknown): void {
    setTakenDown(true);
    // The 403's generic message says nothing; the server's sentence points at support.
    setError(err instanceof ApiError && err.reason ? err.reason : new TakenDownError().message);
  }

  async function publish(): Promise<void> {
    setError(null);
    setBusy(true);
    setNotice(null);
    /*
     * With no link on record the button says "Publish", and pressing it is a decision to put
     * the page UP. With one it says "Publish changes": the content changes, and whether the
     * page is on stays where "Make private" or "Take offline" left it.
     */
    const putOnline = !url;
    try {
      /*
       * With the server unreachable, a private app offers Publish rather than Put it back
       * online, and pressing it means the same: relist, then put the page up.
       */
      const result = await publishOrRelist(putOnline);
      if (result.webPublished === false) {
        setNotice(SAVED_BUT_OFFLINE);
      }
    } catch (err) {
      if (isTakenDown(err)) showTakenDown(err);
      else setError(err instanceof Error ? err.message : 'Could not publish this app.');
    } finally {
      // Always: a stuck spinner is the failure people actually report.
      setBusy(false);
    }
  }

  /**
   * Put a page that is offline back up, at the address it had.
   *
   * Named public explicitly: a content update would leave the page exactly as Take offline left
   * it — off. And it must not report success unless the server says the page is up.
   */
  async function putBackOnline(): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const result = await publishOrRelist(true);
      setNotice(result.webPublished === false ? SAVED_BUT_OFFLINE : 'Your website is back online, at the same address.');
    } catch (err) {
      if (isTakenDown(err)) {
        // Not a failure they can retry, so the retry affordance goes away with it.
        showTakenDown(err);
      } else {
        setError(err instanceof Error ? err.message : 'Your website could not be put back online.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function copy(): Promise<void> {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard permission denied — the link is on screen and selectable anyway */
    }
  }

  /**
   * Take the page off the internet — and out of Discover, when it is in it.
   *
   * The local record is updated ONLY after the server confirms, and never on failure: a
   * project that says "offline" over a page that is still up is the exact bug this exists
   * to prevent, and it is worse than an error message.
   */
  async function takeOffline(): Promise<void> {
    dialog.current?.close();
    setError(null);
    setNotice(null);
    setTakingDown(true);
    try {
      await makePrivate(hostedAppId);
      // Onto the record as stored, not this screen's copy of it: see recordPrivate.
      await recordPrivate(project.id, user?.id ?? '');
      setUrl(null);
      // Out of Discover either way: makePrivate delists it unless the server said it was not in it.
      onSiteChange({ kind: 'known', status: 'offline', listed: false, takenDown: false });
      setNotice('Your website is offline. Your app and everything people saved in it stay as they are.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your website could not be taken offline.');
    } finally {
      setTakingDown(false);
    }
  }

  /*
   * With no page, "Publish". With one, "Publish changes" only when the page does not have this
   * version — or when this record is too old to say, since hiding the button then could strand
   * an edit that never went live. And never while an edit is on its way: it publishes itself.
   *
   * Neither while the server says the page is offline: Put it back online is the one action
   * then, and it publishes this version as it puts the page up. Two buttons that both publish
   * made the owner choose between words for one thing.
   */
  const refused = takenDown || Boolean(known?.takenDown);
  const live = liveState(definition);
  const offerPublish = !editing && !refused && !offline && (!url || live !== 'current');
  /*
   * Take offline whenever the page is not known to be down. While the server has not answered,
   * the record's address is enough to offer it: taking a page down is the safe direction, and
   * makePrivate asks the server itself and acts on both halves when it cannot.
   */
  const offerTakeOffline = Boolean(hostedAppId) && !refused && (known ? !offline : Boolean(url));
  const offerPutBack = Boolean(hostedAppId) && !refused && offline;
  const confirmation = takeOfflineConfirmation(project.name);

  return (
    <section className="publish-panel" aria-labelledby={headingId}>
      <h2 id={headingId} className="type-micro section-label">Your website</h2>
      {(known || offerTakeOffline || site.kind === 'unknown') && (
        <div className="publish-state">
          {known && (
            <p className="type-body publish-state-text">
              <Chip
                label={SITE_STATUS_LABEL[known.status]}
                tone={known.status === 'live' ? 'success' : known.status === 'password' ? 'primary' : 'neutral'}
              />{' '}
              <span className="type-caption">{SITE_STATUS_MEANING[known.status]}</span>
            </p>
          )}
          {site.kind === 'unknown' && (
            // No status word from a failed lookup: a guess here is a claim about who can open it.
            <p className="type-caption publish-state-text">We couldn&rsquo;t check whether your website is online just now.</p>
          )}
          {/*
            * Both wait for an edit on its way, and each for the other. Whichever lands second
            * writes onto the record after the other, and a page taken down under a publish that
            * then wrote its address back read as live while serving nothing.
            */}
          {offerTakeOffline && (
            <Button
              title="Take offline"
              variant="dangerSoft"
              loading={takingDown}
              disabled={editing || busy}
              onPress={() => dialog.current?.showModal()}
            />
          )}
          {offerPutBack && (
            <Button
              title="Put it back online"
              loading={busy}
              disabled={editing || takingDown}
              onPress={() => void putBackOnline()}
            />
          )}
        </div>
      )}
      {known?.takenDown && !takenDown && (
        <p role="alert" className="type-caption field-error-text">{TAKEN_DOWN_MESSAGE}</p>
      )}
      {url && !offline && (
        <p className="type-body publish-url">
          <a href={url} target="_blank" rel="noreferrer">{url}</a>
        </p>
      )}
      {url && offerPublish && live === 'behind' && (
        <p className="type-caption">The live page doesn't have your latest change yet.</p>
      )}
      {url && offerPublish && live === 'unknown' && (
        <p className="type-caption">The live page may be older than this version.</p>
      )}
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
      {notice && <p role="status" className="type-caption">{notice}</p>}
      <div className="publish-actions">
        {offerPublish && (
          <Button
            title={url ? 'Publish changes' : 'Publish'}
            variant={url ? 'secondary' : 'primary'}
            onPress={() => void publish()}
            loading={busy}
          />
        )}
        {url && !offline && <Button title={copied ? 'Copied' : 'Copy link'} variant="ghost" onPress={() => void copy()} />}
      </div>

      <dialog ref={dialog} aria-label={confirmation.title} className="confirm-dialog">
        <h2 className="type-title">{confirmation.title}</h2>
        <p className="type-body">{confirmation.body}</p>
        {/* Only when the server says it is there: most apps built here were never in Discover. */}
        {known?.listed && !offline && (
          <p className="type-body">It comes off Discover too, and goes back when you put it back online.</p>
        )}
        <div className="build-actions">
          <Button title={confirmation.confirm} variant="dangerSoft" onPress={() => void takeOffline()} />
          <Button title="Cancel" variant="ghost" onPress={() => dialog.current?.close()} />
        </div>
      </dialog>
    </section>
  );
}
